"use client";
import React, { Suspense } from "react";
import Card from "../primitives/card";
import { ALL_SPORT_OPTIONS } from "@/utils/sports";
import { ALL_ODD_OPTIONS } from "@/data/sports/options";
import PickHistoryTable from "./pick-history-table";
import NotFound from "../primitives/not-found";
import Select from "../primitives/select";
import {
  BettorHistoricalPick,
  BettorHistoricalPicks,
  BettorInfo,
  LINE_TYPE_OPTIONS,
  LineType,
  OddVariant,
  PaginatedResponse,
  SPORT_TYPE_OPTIONS,
  Sport,
  SportType,
} from "common";
import useFetch from "@/utils/fetch";
import TableFallback from "../primitives/table-fallback";

export interface PickHistorySearchParams {
  index?: string;
  rows?: string;
  cursor?: string;
  dir?: string;
}
export default function PickHistory({
  info,
}: {
  info: BettorInfo;
}) {

  const [selectedSport, setSelectedSport] = React.useState<"all-sports" | Sport>("all-sports");
  const [selectedOdd, setSelectedOdd] = React.useState<OddVariant>("all");
  const [page, setPage] = React.useState<number>(1);
  const [limit, setLimit] = React.useState<number>(10);
  const baseUrl: string = `${process.env.NEXT_PUBLIC_API_URL}/bettors/${info.id}/picks/history`;
  const params: URLSearchParams = new URLSearchParams();
  if (selectedSport !== "all-sports") {
    params.append("sport", selectedSport);
  }
  if (selectedOdd !== "all") {
    params.append("odd", selectedOdd);
  }
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  const request: string = `${baseUrl}?${params}`;
  const { data, isLoading, isError, error } = useFetch<
    BettorHistoricalPicks | undefined
  >(undefined, request, []);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newSize: number) => {
    setLimit(newSize);
    setPage(1);
  };

  const onSportChange = (v: string) => {
    setSelectedSport(v as Sport);
    setPage(1);
  };

  const onOddChange = (v: string) => {
    setSelectedOdd(v as OddVariant);
    setPage(1);
  };

  return (
    <Card.Group>
      <Card
        header={{
          title: "Pick History",
          action: (
            <div className="flex gap-4">
              <Select
                options={ALL_SPORT_OPTIONS}
                value={selectedSport}
                onValueChange={onSportChange}
                label="Sports"
                variant="card"
              />
              <Select
                options={ALL_ODD_OPTIONS}
                value={selectedOdd}
                onValueChange={onOddChange}
                label="Odds"
                neutral
                variant="card"
              />
            </div>
          ),
        }}
      >
        <PickHistoryTable
          data={(data === null || data?.picks === null || data?.picks.length === 0 || isError) ? undefined : data?.picks}
          error={isError}
          fallback={
            <div className="h-96">
              <NotFound
                label="no picks found"
                icon="ticket-buy"
                caption="Picks of concluded games will appear here."
              />
            </div>
          }
          pagination={{
            size: limit,
            page: page,
            count: data?.pagination.totalCount || 0,
            onPageChange: onPageChange,
            onPageSizeChange: onPageSizeChange,
          }}
        />
      </Card>
      <div className="bg-surface rounded-lg w-full p-8 flex flex-col gap-4">
        <div className="text-label-lg-bold uppercase text-secondary">
          Glossary
        </div>
        <div className="flex flex-row flex-wrap text-title-sm text-secondary-300 gap-y-4 py-4 whitespace-nowrap gap-4">
          <div className="flex basis-3/12">
            <span className="font-medium">W:</span>&nbsp;<span>Win</span>
          </div>
          <div className="flex basis-3/12">
            <span className="font-medium">L:</span>&nbsp;<span>Loss</span>
          </div>
          <div className="flex basis-3/12">
            <span className="font-medium">P:</span>&nbsp;<span>Push</span>
          </div>
          <div className="flex basis-3/12">
            <span className="font-medium">PP:</span>&nbsp;
            <span>Postponed Game</span>
          </div>
          <div className="flex basis-3/12">
            <span className="font-medium">C:</span>&nbsp;
            <span>Canceled Game</span>
          </div>
        </div>
      </div>
    </Card.Group>
  );
}
